/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";
import { University } from "@/store/types/universityTypes";
import { useEffect, useState } from "react";
import { Close, GreenAirplane, ReqIcon } from "../common/Icons";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";

const EntryRequirements = ({
  universityDetail,
}: {
  universityDetail: University;
}) => {
  const [activeTab, setActiveTab] = useState("qualifications");
  const [englishTests, setEnglishTests] = useState<ProficiencyTest[]>([]);
  const [selectedTestIndex, setSelectedTestIndex] = useState<number>(0);
  const [englishWaiver, setEnglishWaiver] = useState(false);
  const [searchBoard, setSearchBoard] = useState("");
  const [englishProficiencyRequirements, setEnglishProficiencyRequirements] =
    useState<QualificationRequirement[]>([]);

  const [qualificationsTab, setQualificationTab] = useState("");

  useEffect(() => {
    if (qualificationsTab) {
      // Convert object into array of EnglishTest type
      const converted = englishProficiencyRequirements
        .filter((item) => item.qualification === qualificationsTab)
        .map((item) => item.languageProficiency);
      setEnglishTests(converted[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qualificationsTab]);

  const selectedTest = englishTests[selectedTestIndex];

  // Parse and filter the array
  const boards: string[] = universityDetail?.englishWaiverStateBoardsAccepted
    ? JSON.parse(universityDetail.englishWaiverStateBoardsAccepted)
    : [];

  const filteredBoards = boards.filter((item) =>
    item.toLowerCase().includes(searchBoard.toLowerCase()),
  );

  useEffect(() => {
    const structuredData = transformProficiencyData(universityDetail);
    setQualificationTab(
      structuredData.length > 0 ? structuredData[0].qualification : "",
    );
    setEnglishProficiencyRequirements(structuredData);
  }, [universityDetail]);

  return (
    <div className="pt-[32px] sm:pt-[40px] md:pt-[46px] lg:pt-[56px]">
      {englishWaiver && (
        <div className="fixed top-0 left-0 min-w-screen min-h-screen z-[99]  flex items-center justify-center">
          <span
            className="min-h-screen min-w-screen bg-black/50 absolute top-0 left-0 pt-10"
            onClick={() => {
              setEnglishWaiver(false);
              document.body.classList.remove("overflow-hidden");
            }}
          ></span>
          <span className="bg-white p-5 sm:p-6 md:p-7 lg:p-8 xl:p-9 3xl:p-10 rounded-2xl max-w-[700px] w-full h-full m-5 font-medium pt-10 text-lg z-20 max-h-[700px] overflow-auto relative">
            <span
              className="max-w-[20px] inline-block w-full right-4 top-4 absolute cursor-pointer"
              onClick={() => {
                setEnglishWaiver(false);
                document.body.classList.remove("overflow-hidden");
              }}
            >
              <Close />
            </span>
            <div className="mt-5">
              <Input
                value={searchBoard}
                placeholder="Search Boards"
                className="border border-black"
                onChange={(e) => setSearchBoard(e.target.value)}
              />
            </div>
            <div className="mt-5">
              {filteredBoards.map((item: string, index: number) => (
                <p key={index} className="text-base sm:text-lg font-semibold">
                  {item}
                </p>
              ))}
              {filteredBoards.length === 0 && (
                <p className="text-base sm:text-lg font-semibold">
                  No Boards Found
                </p>
              )}
            </div>
          </span>
        </div>
      )}
      <div className="bg-white p-5 sm:p-6 md:p-7 xl:p-[32px] rounded-[24px] shadow-[0px_4px_20px_0px_#00000040]">
        <p className="font-semibold text-[18px] sm:text-[22px] md:text-[24px] 2xl:text-[32px] mb-3 flex items-center gap-3">
          <ReqIcon /> University Entry Requirements
        </p>
        <p className="text-[#A0A0A0] ff_poppins text-sm sm:text-lg xl:text-xl 2xl:text-2xl">
          Review the minimum academic and English language qualifications needed
          to apply for this university.
        </p>

        <div className="flex items-center gap-6 py-6 overflow-x-auto">
          {englishProficiencyRequirements.map((item, index) => (
            <div
              key={index}
              className="py-2 sm:py-3 px-3 sm:px-6 relative cursor-pointer"
              onClick={() => setQualificationTab(item.qualification)}
            >
              <span
                className={`w-full h-1 absolute left-0 bottom-0 duration-300 ease-in-out ${
                  qualificationsTab === item.qualification
                    ? "bg-[#3777FF]"
                    : "bg-black"
                }`}
              ></span>
              <p className="ff_poppins font-medium text-base sm:text-lg capitalize text-nowrap">
                For{" "}
                {item.qualification === "Pg"
                  ? "Masters"
                  : item.qualification === "Ug"
                    ? "Bachelors"
                    : item.qualification}
              </p>
            </div>
          ))}
        </div>

        <div className="flex sm:items-center flex-col sm:flex-row mb-3 gap-4 sm:gap-6">
          {englishTests.length > 0 && (
            <button
              onClick={() => setActiveTab("language")}
              className={`ff_poppins font-semibold rounded-3xl px-6 py-3 transition-colors duration-300 ${
                activeTab === "language"
                  ? "bg-[#87ADFF] text-white"
                  : "bg-[#E0E0E0] text-black"
              }`}
            >
              Language Proficiency
            </button>
          )}
          <button
            onClick={() => setActiveTab("qualifications")}
            className={`ff_poppins font-semibold rounded-3xl px-6 py-3 transition-colors duration-300 ${
              activeTab === "qualifications"
                ? "bg-[#87ADFF] text-white"
                : "bg-[#E0E0E0] text-black"
            }`}
          >
            Qualifications
          </button>
        </div>

        {activeTab === "language" ? (
          <>
            <p className="text-sm sm:text-base md:text-lg ff_poppins font-semibold my-6 text-black">
              Choose your language proficiency test
            </p>

            {englishTests.length > 0 ? (
              <>
                <div className="flex items-center gap-4 sm:gap-5 md:gap-6 lg:gap-[28px] xl:gap-[38px] 3xl:gap-[42px] mb-6 flex-wrap">
                  {englishTests.map((test, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 cursor-pointer"
                      onClick={() => setSelectedTestIndex(index)}
                    >
                      <Checkbox
                        id={`test-${index}`}
                        checked={selectedTestIndex === index}
                      />
                      <label
                        htmlFor={`test-${index}`}
                        className="ff_poppins font-semibold text-sm sm:text-base lg:text-lg inline-block cursor-pointer"
                      >
                        {test.test}
                      </label>
                    </div>
                  ))}

                  {/* Custom option: I don't have any */}
                  {universityDetail.englishWaiverAccepted && (
                    <div
                      className="flex items-center gap-3 cursor-pointer"
                      onClick={() =>
                        setSelectedTestIndex(englishTests.length + 1)
                      }
                    >
                      <Checkbox
                        id={`test-${englishTests.length + 1}`}
                        checked={selectedTestIndex === englishTests.length + 1}
                      />
                      <label
                        htmlFor={`test-${englishTests.length + 1}`}
                        className="ff_poppins font-semibold text-sm sm:text-base lg:text-lg inline-block cursor-pointer"
                      >
                        I don&#39;t have any
                      </label>
                    </div>
                  )}
                </div>

                {/* Render test scores if a real test is selected */}
                {selectedTestIndex !== englishTests.length + 1 &&
                  selectedTest && (
                    <div className="flex items-center justify-start gap-9 sm:gap-x-16 gap-y-5 flex-wrap text-base ff_poppins">
                      {selectedTest.marks.map((item, index) => (
                        <div className="flex items-center gap-3" key={index}>
                          <GreenAirplane />
                          <p className="font-semibold text-sm sm:text-base lg:text-lg ff_poppins">
                            {item.test} - {item.marks || "N/A"}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                {/* Custom content when no test is selected */}
                {selectedTestIndex === englishTests.length + 1 && (
                  <div className="flex items-center justify-start gap-9 sm:gap-x-16 gap-y-5 flex-wrap">
                    {universityDetail?.twelfthEnglishMarks ? (
                      <div className="flex items-center gap-3">
                        <GreenAirplane />
                        <p className="font-semibold text-lg ff_poppins">
                          12th English -{" "}
                          {`${
                            String(universityDetail?.twelfthEnglishMarks).split(
                              ".",
                            )[1]
                          }%` || "N/A"}
                        </p>
                      </div>
                    ) : (
                      "To be Announced"
                    )}
                    {universityDetail?.englishWaiverAccepted &&
                      filteredBoards.length > 0 &&
                      universityDetail?.addressInfo?.country.toLowerCase() ===
                        "united kingdom" && (
                        <div className="flex items-center gap-3">
                          <GreenAirplane />
                          <p
                            className="font-semibold text-lg ff_poppins"
                            onClick={() => {
                              setEnglishWaiver(true);
                              document.body.classList.add("overflow-hidden");
                            }}
                          >
                            English Waiver -{" "}
                            <button className="underline cursor-pointer">
                              {" "}
                              Boards Accepted
                            </button>
                          </p>
                        </div>
                      )}
                  </div>
                )}
              </>
            ) : (
              <p className="text-gray-500 italic mt-4">To be announced</p>
            )}
          </>
        ) : (
          <>
            {qualificationsTab === "Certificate" ? (
              universityDetail?.tenthPercentage ? (
                <div className="flex items-center gap-3">
                  <GreenAirplane />
                  <p className="font-semibold text-lg ff_poppins">
                    10th Score -{" "}
                    {`${String(universityDetail?.tenthPercentage).split(".")[1]}%` ||
                      "N/A"}
                  </p>
                </div>
              ) : (
                <p className="text-gray-500 italic">To be announced</p>
              )
            ) : (
              <div className="flex items-center justify-start gap-x-16 gap-y-5 flex-wrap text-base ff_poppins mt-5">
                {!universityDetail?.tenthPercentage &&
                !universityDetail?.twelfthPercentagePg &&
                !universityDetail?.twelfthPercentageUg &&
                !universityDetail?.twelfthEnglishMarks ? (
                  <p className="text-gray-500 italic">To be announced</p>
                ) : (
                  <>
                    {universityDetail?.tenthPercentage && (
                      <div className="flex items-center gap-3">
                        <GreenAirplane />
                        <p className="font-semibold text-lg ff_poppins">
                          10th Score -{" "}
                          {`${
                            String(universityDetail?.tenthPercentage).split(
                              ".",
                            )[1]
                          }%` || "N/A"}
                        </p>
                      </div>
                    )}
                    {universityDetail?.twelfthEnglishMarks && (
                      <div className="flex items-center gap-3">
                        <GreenAirplane />
                        <p className="font-semibold text-lg ff_poppins">
                          12th English -{" "}
                          {`${
                            String(universityDetail?.twelfthEnglishMarks).split(
                              ".",
                            )[1]
                          }%` || "N/A"}
                        </p>
                      </div>
                    )}
                    {qualificationsTab === "Ug" ||
                    qualificationsTab === "diploma"
                      ? universityDetail?.twelfthPercentageUg &&
                        universityDetail?.twelfthPercentageUg &&
                        Object.entries(
                          universityDetail.twelfthPercentageUg,
                        ).map(([board, percentage]) => (
                          <div key={board} className="flex items-center gap-3">
                            <span>
                              <GreenAirplane className="size-[20px]" />
                            </span>
                            <p className="font-semibold text-base sm:text-lg ff_poppins">
                              12th {board} - {(percentage * 100).toFixed(0)}%
                            </p>
                          </div>
                        ))
                      : universityDetail?.twelfthPercentagePg &&
                        universityDetail?.twelfthPercentagePg &&
                        Object.entries(
                          universityDetail.twelfthPercentagePg,
                        ).map(([board, percentage]) => (
                          <div key={board} className="flex items-center gap-3">
                            <span>
                              <GreenAirplane className="size-[20px]" />
                            </span>
                            <p className="font-semibold text-base sm:text-lg ff_poppins">
                              12th {board} - {(percentage * 100).toFixed(0)}%
                            </p>
                          </div>
                        ))}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EntryRequirements;

// 1. Define the Types
// ---------------------------------------------------------
export interface ScoreItem {
  test: string;
  marks: number;
}

export interface ProficiencyTest {
  test: string;
  marks: ScoreItem[];
}

export interface QualificationRequirement {
  qualification: string;
  languageProficiency: ProficiencyTest[];
}

// 2. The Transformation Function
// ---------------------------------------------------------
export function transformProficiencyData(
  data: Record<string, any>,
): QualificationRequirement[] {
  const prefix = "englishProficiencyRequirements";
  const result: QualificationRequirement[] = [];

  Object.keys(data).forEach((key) => {
    if (key.startsWith(prefix) && data[key] !== null) {
      const qualificationName = key.replace(prefix, "").trim();

      const rawExams = data[key];

      const languageProficiency: ProficiencyTest[] = Object.keys(rawExams).map(
        (examName) => {
          const rawScores = rawExams[examName];

          const marksArray: ScoreItem[] = Object.entries(rawScores).map(
            ([scoreName, scoreValue]) => {
              return {
                test: scoreName,
                marks: Number(scoreValue),
              };
            },
          );

          return {
            test: examName,
            marks: marksArray,
          };
        },
      );
      result.push({
        qualification: qualificationName,
        languageProficiency: languageProficiency,
      });
    }
  });

  return result;
}
